import React, { useRef, useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { 
  generatePDFFromElement, 
  generateSectionReportPDF
} from "../../../utils/invoiceUtils";

const ViewFinancialReportModal = ({ 
  isOpen, 
  onClose, 
  studentId, 
  sectionId, 
  isSectionReport, 
  sectionName, 
  className,
  campusName
}) => {
  const reportRef = useRef();
  const [loading, setLoading] = useState(false);

  // Dummy financial data for individual student
  const dummyFinancialReport = {
    studentId: "STU001",
    fullName: "John Doe",
    classGrade: "Grade 10",
    sectionName: "Section A",
    financialDetails: [
      {
        feeCategory: "Tuition Fee",
        feeAmount: 5000,
        amountPaid: 3000,
        balanceDue: 2000,
        paymentDates: ["2024-01-15", "2024-02-15"],
        paymentMethod: "Bank Transfer",
        status: "partial"
      },
      {
        feeCategory: "Library Fee",
        feeAmount: 500,
        amountPaid: 500,
        balanceDue: 0,
        paymentDates: ["2024-01-10"],
        paymentMethod: "Cash",
        status: "paid"
      },
      {
        feeCategory: "Sports Fee",
        feeAmount: 300,
        amountPaid: 0,
        balanceDue: 300,
        paymentDates: null,
        paymentMethod: null,
        status: "pending"
      },
      {
        feeCategory: "Lab Fee",
        feeAmount: 800,
        amountPaid: 400,
        balanceDue: 400,
        paymentDates: ["2024-01-20"],
        paymentMethod: "Credit Card",
        status: "partial"
      }
    ]
  };

  // Dummy section financial reports
  const dummySectionReports = [
    {
      financialDetails: [
        {
          studentId: "STU001",
          fullName: "John Doe",
          classGrade: "Grade 10",
          feeCategory: "Tuition Fee",
          feeAmount: 5000,
          amountPaid: 3000,
          balanceDue: 2000,
          paymentDates: ["2024-01-15", "2024-02-15"],
          paymentMethod: "Bank Transfer",
          status: "partial"
        },
        {
          studentId: "STU002",
          fullName: "Jane Smith",
          classGrade: "Grade 10",
          feeCategory: "Tuition Fee",
          feeAmount: 5000,
          amountPaid: 5000,
          balanceDue: 0,
          paymentDates: ["2024-01-10"],
          paymentMethod: "Bank Transfer",
          status: "paid"
        },
        {
          studentId: "STU003",
          fullName: "Mike Johnson",
          classGrade: "Grade 10",
          feeCategory: "Tuition Fee",
          feeAmount: 5000,
          amountPaid: 0,
          balanceDue: 5000,
          paymentDates: null,
          paymentMethod: null,
          status: "overdue"
        },
        {
          studentId: "STU004",
          fullName: "Sarah Wilson",
          classGrade: "Grade 10",
          feeCategory: "Tuition Fee",
          feeAmount: 5000,
          amountPaid: 2500,
          balanceDue: 2500,
          paymentDates: ["2024-01-05"],
          paymentMethod: "Cash",
          status: "partial"
        }
      ]
    }
  ];

  const financialReport = dummyFinancialReport;
  const sectionReports = dummySectionReports;

  // Simulate loading for better UX
  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      // Simulate API call delay
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  }, [isOpen]);

  const downloadPDF = () => {
    if (isSectionReport) {
      generateSectionReportPDF(
        sectionReports,
        className,
        sectionName,
        campusName,
        "Section_Financial_Report.pdf"
      );
    } else {
      // Use generatePDFFromElement for individual financial reports
      generatePDFFromElement(reportRef, "Financial_Report.pdf");
    }
  };

  const downloadExcel = () => {
    if (isSectionReport) {
      if (!sectionReports || !sectionReports.length) return;
      
      const workbook = XLSX.utils.book_new();
      const summaryData = [
        ["Financial Section Report"],
        ["Campus", campusName],
        ["Class", className],
        ["Section", sectionName],
        [],
        ["Student ID", "Full Name", "Class/Grade", "Fee Category", "Fee Amount", "Amount Paid", "Balance Due", "Payment Date(s)", "Payment Method", "Status"],
      ];
      
      sectionReports.forEach(report => {
        if (report.financialDetails && report.financialDetails.length > 0) {
          report.financialDetails.forEach(financial => {
            summaryData.push([
              financial.studentId || "N/A",
              financial.fullName || "N/A",
              financial.classGrade || className,
              financial.feeCategory || "N/A",
              financial.feeAmount || 0,
              financial.amountPaid || 0,
              financial.balanceDue || 0,
              financial.paymentDates ? financial.paymentDates.join(", ") : "N/A",
              financial.paymentMethod || "N/A",
              financial.status || "Pending"
            ]);
          });
        }
      });
      
      const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
      XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");
      
      XLSX.writeFile(workbook, "Section_Financial_Report.xlsx");
    } else {
      if (!financialReport) return;

      const report = Array.isArray(financialReport) ? financialReport[0] : financialReport;
      if (!report) return;

      const worksheetData = [
        ["Financial Report"],
        ["Student ID", report.studentId],
        ["Student Name", report.fullName],
        ["Campus", campusName],
        ["Class", report.classGrade],
        ["Section", report.sectionName],
        [],
        ["Fee Category", "Fee Amount", "Amount Paid", "Balance Due", "Payment Date(s)", "Payment Method", "Status"],
      ];

      if (report.financialDetails && Array.isArray(report.financialDetails)) {
        worksheetData.push(
          ...report.financialDetails.map((financial) => [
            financial.feeCategory || "N/A",
            financial.feeAmount || 0,
            financial.amountPaid || 0,
            financial.balanceDue || 0,
            financial.paymentDates ? financial.paymentDates.join(", ") : "N/A",
            financial.paymentMethod || "N/A",
            financial.status || "Pending"
          ])
        );
      }

      // Add totals
      const totalFeeAmount = report.financialDetails?.reduce((sum, item) => sum + (item.feeAmount || 0), 0) || 0;
      const totalAmountPaid = report.financialDetails?.reduce((sum, item) => sum + (item.amountPaid || 0), 0) || 0;
      const totalBalanceDue = report.financialDetails?.reduce((sum, item) => sum + (item.balanceDue || 0), 0) || 0;
      
      worksheetData.push(["Total", totalFeeAmount, totalAmountPaid, totalBalanceDue, "", "", ""]);

      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Financial Report");
      XLSX.writeFile(workbook, "Financial_Report.xlsx");
    }
  };

  if (!isOpen) return null;

  const reportData = !isSectionReport && financialReport ? (Array.isArray(financialReport) ? financialReport[0] : financialReport) : null;

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'paid': return 'text-green-600 bg-green-50 border-green-200';
      case 'partial': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
      case 'pending': return 'text-red-600 bg-red-50 border-red-200';
      case 'overdue': return 'text-red-800 bg-red-100 border-red-300';
      default: return 'text-gray-600 bg-gray-50 border-gray-200';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div ref={reportRef} className="bg-white p-6 rounded-lg shadow-lg w-[95%] max-h-[90vh] overflow-y-auto relative border border-gray-300">
        {loading && <p>Loading report...</p>}

        {isSectionReport ? (
          <>
            {sectionReports && sectionReports.length > 0 ? (
              <div>
                {/* Section Report Header */}
                <div className="text-center mb-6 border-b pb-4">
                  <h1 className="text-2xl font-bold text-gray-800">{campusName}</h1>
                  <h2 className="text-xl font-semibold mt-2">Financial Section Report</h2>
                  <div className="grid grid-cols-2 gap-4 mt-4 text-sm">
                    <div><strong>Class:</strong> {className}</div>
                    <div><strong>Section:</strong> {sectionName}</div>
                    <div><strong>Generated On:</strong> {new Date().toLocaleDateString('en-GB')}</div>
                  </div>
                </div>

                {/* Financial Summary */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-4">Summary</h3>
                  <div className="grid grid-cols-4 gap-4 text-center">
                    <div className="bg-blue-50 p-4 rounded">
                      <div className="text-2xl font-bold text-blue-600">
                        {sectionReports.reduce((total, report) => total + (report.financialDetails?.length || 0), 0)}
                      </div>
                      <div className="text-sm text-gray-600">Total Students</div>
                    </div>
                    <div className="bg-green-50 p-4 rounded">
                      <div className="text-2xl font-bold text-green-600">
                        {formatCurrency(sectionReports.reduce((total, report) => 
                          total + (report.financialDetails?.reduce((sum, item) => sum + (item.amountPaid || 0), 0) || 0), 0
                        ))}
                      </div>
                      <div className="text-sm text-gray-600">Total Paid</div>
                    </div>
                    <div className="bg-red-50 p-4 rounded">
                      <div className="text-2xl font-bold text-red-600">
                        {formatCurrency(sectionReports.reduce((total, report) => 
                          total + (report.financialDetails?.reduce((sum, item) => sum + (item.balanceDue || 0), 0) || 0), 0
                        ))}
                      </div>
                      <div className="text-sm text-gray-600">Total Outstanding</div>
                    </div>
                    <div className="bg-yellow-50 p-4 rounded">
                      <div className="text-2xl font-bold text-yellow-600">
                        {sectionReports.reduce((total, report) => 
                          total + (report.financialDetails?.filter(f => f.status === 'partial')?.length || 0), 0
                        )}
                      </div>
                      <div className="text-sm text-gray-600">Partial Payments</div>
                    </div>
                  </div>
                </div>

                {/* Detailed Financial List */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Detailed Report</h3>
                  {sectionReports.map((report, reportIndex) => (
                    <div key={reportIndex}>
                      {report.financialDetails && report.financialDetails.length > 0 ? (
                        <div className="overflow-x-auto">
                          <table className="w-full border-collapse border border-gray-300">
                            <thead>
                              <tr className="bg-gray-100">
                                <th className="border border-gray-300 px-3 py-2 text-left">Student ID</th>
                                <th className="border border-gray-300 px-3 py-2 text-left">Full Name</th>
                                <th className="border border-gray-300 px-3 py-2 text-left">Class/Grade</th>
                                <th className="border border-gray-300 px-3 py-2 text-left">Fee Category</th>
                                <th className="border border-gray-300 px-3 py-2 text-right">Fee Amount</th>
                                <th className="border border-gray-300 px-3 py-2 text-right">Amount Paid</th>
                                <th className="border border-gray-300 px-3 py-2 text-right">Balance Due</th>
                                <th className="border border-gray-300 px-3 py-2 text-left">Payment Date(s)</th>
                                <th className="border border-gray-300 px-3 py-2 text-left">Payment Method</th>
                                <th className="border border-gray-300 px-3 py-2 text-center">Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              {report.financialDetails.map((financial, index) => (
                                <tr key={index} className="hover:bg-gray-50">
                                  <td className="border border-gray-300 px-3 py-2 font-medium">
                                    {financial.studentId || `STU-${index + 1}`}
                                  </td>
                                  <td className="border border-gray-300 px-3 py-2">
                                    {financial.fullName || "N/A"}
                                  </td>
                                  <td className="border border-gray-300 px-3 py-2">
                                    {financial.classGrade || className}
                                  </td>
                                  <td className="border border-gray-300 px-3 py-2">
                                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                                      {financial.feeCategory || "General"}
                                    </span>
                                  </td>
                                  <td className="border border-gray-300 px-3 py-2 text-right font-medium">
                                    {formatCurrency(financial.feeAmount)}
                                  </td>
                                  <td className="border border-gray-300 px-3 py-2 text-right text-green-600 font-medium">
                                    {formatCurrency(financial.amountPaid)}
                                  </td>
                                  <td className="border border-gray-300 px-3 py-2 text-right text-red-600 font-medium">
                                    {formatCurrency(financial.balanceDue)}
                                  </td>
                                  <td className="border border-gray-300 px-3 py-2 text-sm">
                                    {financial.paymentDates ? 
                                      (Array.isArray(financial.paymentDates) ? 
                                        financial.paymentDates.map(date => new Date(date).toLocaleDateString('en-GB')).join(", ") :
                                        new Date(financial.paymentDates).toLocaleDateString('en-GB')
                                      ) : 
                                      "N/A"
                                    }
                                  </td>
                                  <td className="border border-gray-300 px-3 py-2">
                                    {financial.paymentMethod || "N/A"}
                                  </td>
                                  <td className="border border-gray-300 px-3 py-2 text-center">
                                    <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(financial.status)}`}>
                                      {financial.status || "Pending"}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="text-center py-8 text-gray-500">
                          No financial records found for this section
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500">No reports found for this section</p>
              </div>
            )}
          </>
        ) : reportData ? (
          <>
            {/* Individual Student Financial Report */}
            <div className="max-w-6xl mx-auto">
              {/* Header */}
              <div className="text-center mb-6 border-b pb-4">
                <h1 className="text-2xl font-bold text-gray-800">{campusName}</h1>
                <h2 className="text-xl font-semibold mt-2">Student Financial Report</h2>
                <div className="grid grid-cols-2 gap-4 mt-4 text-sm">
                  <div><strong>Student ID:</strong> {reportData.studentId}</div>
                  <div><strong>Student Name:</strong> {reportData.fullName}</div>
                  <div><strong>Class/Grade:</strong> {reportData.classGrade}</div>
                  <div><strong>Generated On:</strong> {new Date().toLocaleDateString('en-GB')}</div>
                </div>
              </div>

              {/* Financial Summary Cards */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-4">Financial Summary</h3>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="bg-blue-50 p-4 rounded border">
                    <div className="text-2xl font-bold text-blue-600">
                      {formatCurrency(reportData.financialDetails?.reduce((sum, item) => sum + (item.feeAmount || 0), 0) || 0)}
                    </div>
                    <div className="text-sm text-gray-600">Total Fee Amount</div>
                  </div>
                  <div className="bg-green-50 p-4 rounded border">
                    <div className="text-2xl font-bold text-green-600">
                      {formatCurrency(reportData.financialDetails?.reduce((sum, item) => sum + (item.amountPaid || 0), 0) || 0)}
                    </div>
                    <div className="text-sm text-gray-600">Amount Paid</div>
                  </div>
                  <div className="bg-red-50 p-4 rounded border">
                    <div className="text-2xl font-bold text-red-600">
                      {formatCurrency(reportData.financialDetails?.reduce((sum, item) => sum + (item.balanceDue || 0), 0) || 0)}
                    </div>
                    <div className="text-sm text-gray-600">Balance Due</div>
                  </div>
                </div>
              </div>

              {/* Detailed Financial Table */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Detailed Financial Records</h3>
                {reportData.financialDetails && reportData.financialDetails.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-gray-300">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="border border-gray-300 px-3 py-2 text-left">Fee Category</th>
                          <th className="border border-gray-300 px-3 py-2 text-right">Fee Amount</th>
                          <th className="border border-gray-300 px-3 py-2 text-right">Amount Paid</th>
                          <th className="border border-gray-300 px-3 py-2 text-right">Balance Due</th>
                          <th className="border border-gray-300 px-3 py-2 text-left">Payment Date(s)</th>
                          <th className="border border-gray-300 px-3 py-2 text-left">Payment Method</th>
                          <th className="border border-gray-300 px-3 py-2 text-center">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.financialDetails.map((financial, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="border border-gray-300 px-3 py-2">
                              <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                                {financial.feeCategory || "General"}
                              </span>
                            </td>
                            <td className="border border-gray-300 px-3 py-2 text-right font-medium">
                              {formatCurrency(financial.feeAmount)}
                            </td>
                            <td className="border border-gray-300 px-3 py-2 text-right text-green-600 font-medium">
                              {formatCurrency(financial.amountPaid)}
                            </td>
                            <td className="border border-gray-300 px-3 py-2 text-right text-red-600 font-medium">
                              {formatCurrency(financial.balanceDue)}
                            </td>
                            <td className="border border-gray-300 px-3 py-2 text-sm">
                              {financial.paymentDates ? 
                                (Array.isArray(financial.paymentDates) ? 
                                  financial.paymentDates.map(date => new Date(date).toLocaleDateString('en-GB')).join(", ") :
                                  new Date(financial.paymentDates).toLocaleDateString('en-GB')
                                ) : 
                                "N/A"
                              }
                            </td>
                            <td className="border border-gray-300 px-3 py-2">
                              {financial.paymentMethod || "N/A"}
                            </td>
                            <td className="border border-gray-300 px-3 py-2 text-center">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(financial.status)}`}>
                                {financial.status || "Pending"}
                              </span>
                            </td>
                          </tr>
                        ))}
                        {/* Totals Row */}
                        <tr className="bg-gray-50 font-semibold">
                          <td className="border border-gray-300 px-3 py-2">Total</td>
                          <td className="border border-gray-300 px-3 py-2 text-right">
                            {formatCurrency(reportData.financialDetails.reduce((sum, item) => sum + (item.feeAmount || 0), 0))}
                          </td>
                          <td className="border border-gray-300 px-3 py-2 text-right text-green-600">
                            {formatCurrency(reportData.financialDetails.reduce((sum, item) => sum + (item.amountPaid || 0), 0))}
                          </td>
                          <td className="border border-gray-300 px-3 py-2 text-right text-red-600">
                            {formatCurrency(reportData.financialDetails.reduce((sum, item) => sum + (item.balanceDue || 0), 0))}
                          </td>
                          <td className="border border-gray-300 px-3 py-2"></td>
                          <td className="border border-gray-300 px-3 py-2"></td>
                          <td className="border border-gray-300 px-3 py-2"></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    No financial records found
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500">No financial data available</p>
          </div>
        )}

        {/* Download Buttons */}
        <div className="no-print flex justify-end mt-6 space-x-4 border-t pt-4">
          <button onClick={downloadPDF} className="bg-blue-500 hover:bg-blue-600 px-4 py-2 rounded text-white transition-colors">
            Download as PDF
          </button>
          <button onClick={downloadExcel} className="bg-green-500 hover:bg-green-600 px-4 py-2 rounded text-white transition-colors">
            Download as Excel
          </button>
          <button onClick={onClose} className="bg-gray-400 hover:bg-gray-500 px-4 py-2 rounded text-white transition-colors">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ViewFinancialReportModal;